import { VITAL_TYPES, type VitalResponse, type VitalType } from '@emr/shared';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { listVitals } from '../../../api/chart.ts';
import type { DataTableColumn } from '../../../components/DataTable.tsx';
import { formatDateTime } from '../../../lib/dateFormat.ts';
import { isMemberOf } from '../../../lib/isMemberOf.ts';
import { ChartTable } from './ChartTable.tsx';

/**
 * A human label for each of `@emr/shared`'s `VITAL_TYPES`. `Record<VitalType, string>` is the
 * forcing function, matching `AccessPage.tsx`'s `CAPABILITY_COPY`: a vital type added to the shared
 * list without a row here fails the build rather than rendering `heart_rate` verbatim. Takes `t`
 * rather than being a static module-level table, since the label is now locale-dependent.
 */
function vitalTypeLabels(t: TFunction<'patientChartTabs'>): Record<VitalType, string> {
  return {
    heart_rate: t('vitals.types.heartRate'),
    respiratory_rate: t('vitals.types.respiratoryRate'),
    blood_pressure_systolic: t('vitals.types.bloodPressureSystolic'),
    blood_pressure_diastolic: t('vitals.types.bloodPressureDiastolic'),
    body_temperature: t('vitals.types.bodyTemperature'),
    oxygen_saturation: t('vitals.types.oxygenSaturation'),
    height: t('vitals.types.height'),
    weight: t('vitals.types.weight'),
    bmi: t('vitals.types.bmi'),
    pain_score: t('vitals.types.painScore'),
  };
}

/** `vitals.vitalType` is open-but-checked text (`clinical.ts`), not a database enum — the server
 * and this build can disagree about the known set, so an unrecognised value degrades to itself
 * rather than throwing. */
function vitalTypeLabel(value: string, labels: Record<VitalType, string>): string {
  return isMemberOf(VITAL_TYPES, value) ? labels[value] : value;
}

function columnsFor(t: TFunction<'patientChartTabs'>): readonly DataTableColumn<VitalResponse>[] {
  const labels = vitalTypeLabels(t);
  return [
    {
      key: 'type',
      header: t('vitals.columns.vital'),
      // `vitalTypeLabel` falls back to the raw `vitalType` string for a value this build does not
      // recognise (open-but-checked text, `clinical.ts`) — capped defensively like every other
      // column, since that fallback path is exactly the unbounded-content case.
      cell: (vital) => vitalTypeLabel(vital.vitalType, labels),
      wrap: 'anywhere',
      maxWidthCh: 20,
    },
    {
      key: 'value',
      header: t('vitals.columns.value'),
      cell: (vital) => vital.value,
      wrap: 'anywhere',
      maxWidthCh: 16,
    },
    { key: 'unit', header: t('vitals.columns.unit'), cell: (vital) => vital.unit, wrap: 'nowrap' },
    {
      key: 'recorded',
      header: t('vitals.columns.recorded'),
      cell: (vital) => formatDateTime(vital.recordedAt),
      wrap: 'nowrap',
    },
  ];
}

export function VitalsTab({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('patientChartTabs');
  return (
    <ChartTable
      queryKey={['patient', patientId, 'vitals']}
      queryFn={(signal) => listVitals(patientId, signal)}
      columns={columnsFor(t)}
      rowKey={(vital) => vital.id}
      minWidthRem={44}
      emptyMessage={t('vitals.empty')}
      loadingLabel={t('vitals.loading')}
      caption={t('vitals.caption')}
    />
  );
}
